import { Registration, Roster, StaffRolePermissions, Team } from '@la/types';
import { extractJSONFromHTML, sanitizeJSON } from 'lib/utils/dataScrapingUtils';
import { InvitationLinks } from 'domains/RosterManagement/RosterManagement';
import { Team as RosterTeam } from 'domains/RosterManagement/ViewDifferentTeamModal';
import baseApi from './baseApi';

export type RegistrantData = {
  playerId: number;
  programId?: string;
  teamId?: string;
};

export type RegistrationStatus =
  | 'PENDING_INVITE'
  | 'REMOVED'
  | 'SPOT_RESERVED'
  | 'SPOT_PENDING'
  | 'WAITING_LIST';

export type Registrant = {
  avatarUrl: string;
  isPrimaryStaff?: boolean;
  name: string;
  registrationStatus: RegistrationStatus;
  ageGroup?: number;
  role?: string;
  roleId?: number;
  roleName?: string;
  emailAddress?: string;
  parent?: string;
  status?: 'complete' | 'incomplete' | 'pending';
  userId: number;
};

export type RosterPageData = {
  programId: string;
  programType: string;
  teamId: string;
};

export type TeamRoster = {
  team: Team;
  players: Registrant[];
  staff: Registrant[];
  fees: {
    player: string;
    staff: string;
  };
};

export type RosterRegistration = Partial<Registration>;

export const rosterManagementApi = baseApi
  .enhanceEndpoints({ addTagTypes: ['RegistrantProfiles', 'Roster'] })
  .injectEndpoints({
    endpoints: (builder) => ({
      getDifferentTeams: builder.query<RosterTeam[], { siteSubdomain: string }>(
        {
          query: ({ siteSubdomain }) =>
            `/api/member-portal/${siteSubdomain}/rosters/teams`,
        }
      ),
      getInvites: builder.query<
        InvitationLinks,
        { programId?: string; siteSubdomain: string; teamId?: string }
      >({
        query: ({ programId, siteSubdomain, teamId }) =>
          `/api/member-portal/${siteSubdomain}/rosters/inviteUrls?programId=${programId}&teamId=${teamId}`,
      }),
      getRegistrantProfileData: builder.query<any, RegistrantData>({
        query: ({ playerId, programId, teamId }) => ({
          url: `https://${window.location.hostname}/ajax/populatePlayerInfoForEdit?bid=${programId}&teamId=${teamId}&playerId=${playerId}&ngmp_2023_iframe_transition=1`,
          method: 'GET',
          responseHandler: 'text',
        }),
        transformResponse: (html: string) => {
          const rawJSONString = extractJSONFromHTML(
            html,
            'roster-management-edit-profile-data'
          );
          return sanitizeJSON(rawJSONString);
        },
        providesTags: ['RegistrantProfiles'],
      }),
      getSideNavData: builder.query<any, RosterPageData>({
        query: ({ programId, programType, teamId }) => ({
          url: `https://${window.location.hostname}/${programType}/${programId}/teamRoster?teamId=${teamId}&ngmp_2023_iframe_transition=1`,
          method: 'GET',
          responseHandler: 'text',
        }),
        transformResponse: (html: string) => {
          const rawJSONString = extractJSONFromHTML(
            html,
            'roster-management-sidenav'
          );
          return sanitizeJSON(rawJSONString);
        },
      }),
      updateRegistrantProfile: builder.mutation({
        query: ({ body }) => ({
          url: `https://${window.location.hostname}/ajax/addSiteTeamPlayer?ngProgram=true`,
          method: 'POST',
          body: new URLSearchParams(body).toString(),
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
        }),
        invalidatesTags: ['RegistrantProfiles'],
      }),

      getTeamRoster: builder.query<
        TeamRoster,
        { siteSubdomain: string; teamId?: string }
      >({
        query: ({ siteSubdomain, teamId }) =>
          `api/member-portal/${siteSubdomain}/rosters?teamId=${teamId}`,
        providesTags: ['Roster'],
      }),
      getRoster: builder.query<
        Roster,
        {
          siteSubdomain: string;
          siteId: string;
          programId: string;
          teamId: string;
        }
      >({
        query: ({ siteSubdomain, siteId, programId, teamId }) => ({
          url: `api/member-portal/${siteSubdomain}/roster`,
          params: { siteId, programId, teamId },
        }),
        providesTags: ['Roster'],
      }),
      getRosterPermissions: builder.query<
        StaffRolePermissions,
        {
          programId: string;
          siteSubdomain: string;
          teamId: string;
        }
      >({
        query: ({ programId, siteSubdomain, teamId }) => ({
          url: `api/member-portal/${siteSubdomain}/rosters/permissions`,
          params: {
            programId,
            teamId,
          },
        }),
      }),
      removeMemberFromRoster: builder.mutation<
        void,
        {
          siteSubdomain: string;
          programId: string;
          teamId: string;
          userId: string;
        }
      >({
        query: ({ siteSubdomain, programId, teamId, userId }) => ({
          url: `api/member-portal/${siteSubdomain}/rosters/removePlayer`,
          method: 'POST',
          body: { programId, teamId, userId },
        }),
        invalidatesTags: ['Registration', 'Roster'],
      }),
      updateRosterMembers: builder.mutation<
        void,
        {
          siteSubdomain: string;
          programId: number;
          content: {
            add?: RosterRegistration[];
            update?: RosterRegistration[];
            remove?: RosterRegistration[];
          };
        }
      >({
        query: ({ siteSubdomain, programId, content }) => ({
          url: `api/member-portal/${siteSubdomain}/registrations`,
          method: 'POST',
          body: {
            programId,
            content,
          },
        }),
        invalidatesTags: ['Registration', 'Roster'],
      }),
      getCSVBuilderStatus: builder.query<string, void>({
        query: () => ({
          method: 'GET',
          url: `https://${window.location.hostname}/ajax/export/status`,
          responseHandler: (response) => response.text(),
        }),
      }),
      getCSVDownloadStatus: builder.query<string | null, string>({
        query: () => ({
          method: 'POST',
          url: `https://${window.location.hostname}/ajax/export/progress`,
          responseHandler: (response) => response.text(),
        }),
        transformResponse: (html: string) => {
          // the <statusValue> part here is giving a "name" to the capture group
          // so we can extract and only return that part of the regex match
          // https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/String/match#using_named_capturing_groups
          const responseValueRegex =
            /value="(?<statusValue>failure|complete|processing)"/;
          return html.match(responseValueRegex)?.groups?.statusValue ?? null;
        },
      }),
      triggerCSVDownload: builder.mutation<
        null,
        {
          body: FormData;
          programId: string;
          programType: string;
        }
      >({
        query: ({ body, programId, programType }) => ({
          body: body,
          formData: true,
          method: 'POST',
          url: `https://${window.location.hostname}/${programType}/${programId}/teamRoster`,
        }),
      }),
      uploadFile: builder.mutation<
        { success: boolean; uuid: string },
        {
          formData: FormData;
          siteId: string;
        }
      >({
        query: ({ formData, siteId }) => ({
          url: `api/sites/${siteId}/file`,
          method: 'POST',
          body: formData,
        }),
      }),
      updateRoster: builder.mutation<
        void,
        {
          siteSubdomain: string;
          programId: number;
          content: {
            add?: RosterRegistration[];
            update?: RosterRegistration[];
            remove?: RosterRegistration[];
          };
        }
      >({
        query: ({ siteSubdomain, programId, content }) => ({
          url: `api/member-portal/${siteSubdomain}/registrations`,
          method: 'POST',
          body: {
            programId,
            content,
          },
        }),
        invalidatesTags: ['Registration', 'Roster'],
      }),
    }),
  });

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  useGetCSVBuilderStatusQuery,
  useGetCSVDownloadStatusQuery,
  useGetDifferentTeamsQuery,
  useGetInvitesQuery,
  useGetRegistrantProfileDataQuery,
  useGetSideNavDataQuery,
  useGetTeamRosterQuery,
  useLazyGetTeamRosterQuery,
  useGetRosterQuery,
  useLazyGetRosterQuery,
  useGetRosterPermissionsQuery,
  useRemoveMemberFromRosterMutation,
  useUpdateRosterMembersMutation,
  useTriggerCSVDownloadMutation,
  useUpdateRegistrantProfileMutation,
  useUploadFileMutation,
  useUpdateRosterMutation,
} = rosterManagementApi;
